import { observable, computed, action, makeObservable } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { FiltersType } from 'src/domains/layouts/webview/components/filters/Filters';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventsCollectionList, EventsCollectionState } from 'src/domains/sportsbook/shared/Types';
import { SliderState } from './racesSlider/Slider.state';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';

export type RaceSportType = 'horseracing' | 'greyhoundracing';
export type RacesRegionType = 'uk&ireland' | 'all';

const sortSmallEventsByTime = (outArray: Array<EventListGroupEventItemType>): Array<EventListGroupEventItemType> => {
    return outArray.sort((a: EventListGroupEventItemType, b: EventListGroupEventItemType): number => {
        const time1 = a.startTime;
        const time2 = b.startTime;

        if (time1 < time2) {
            return -1;
        }
        if (time1 > time2) {
            return 1;
        }

        return 0;
    });
};

const getEventsForSportType = (
    common: Common,
    sport: RaceSportType,
    isBetGliderActive: boolean
): EventsCollectionList => {
    const eventsCollection = EventsCollectionState.get(common);

    if (sport === 'horseracing') {
        if (isBetGliderActive) {
            return eventsCollection
                .getListNextOffRacingWithoutFinishState('horseracing')
                .filterBySportAndEvent((_sport, event): boolean => {
                    return event.tags['specials'] !== 'yes';
                });
        }

        const races = eventsCollection.getRaces('horseracing');
        return races.filterBySportAndEvent((_sport, event): boolean => {
            return event.tags['specials'] !== 'yes';
        });
    } else {
        return eventsCollection.getRaces('greyhoundracing');
    }
};

const PAGE_SIZE = 10;

const isRacingEventVisibleForNextRacesCarousel = (event: EventModel, shouldDisplayStartedRaces: boolean): boolean => {
    const isStartedRaceForBetGlider = shouldDisplayStartedRaces && event.timeSettingsTimeline === 'started';
    const isVisibleForCarousel = event.display || isStartedRaceForBetGlider;
    const shouldExcludeStartedRace = !shouldDisplayStartedRaces && event.timeSettingsStarted;

    if (event.timeSettingsTimeline === 'finished' || shouldExcludeStartedRace) {
        return false;
    }

    return isVisibleForCarousel;
};

/* ----------> SLIDER DATA STATE <---------- */
export class SliderDataState {
    public static get = AutoWeakMap.create(
        (common: Common, activeSportTab: RaceSportType) => new SliderDataState(common, activeSportTab)
    );

    private readonly configComponents: ConfigComponents;

    @observable private isFirstLoadCompeted: boolean = false;

    @observable pageSize: number = PAGE_SIZE;
    @observable page: number = 1;

    private constructor(
        private readonly common: Common,
        private readonly activeSportTab: RaceSportType
    ) {
        this.configComponents = ConfigComponents.get(common);

        setTimeout(
            action(() => {
                this.isFirstLoadCompeted = true;
            }),
            3000
        );

        makeObservable(this);
    }

    @computed public get isFirstHRLoadingCompleted(): boolean {
        if (this.configComponents.config.optimizeHRCarousel) {
            if (this.isFirstLoadCompeted) {
                return true;
            }
            return this.isLoadingEventsCollectionEarly === false;
        }
        return true;
    }

    @computed public get isLoadingEventsCollectionEarly(): boolean {
        if (this.isFirstLoadCompeted) {
            return false;
        }

        for (const eventModel of this.eventModels.slice(0, 3)) {
            const eventMarkets = eventModel.id2.getEventMarkets();

            if (eventMarkets === null) {
                return true;
            }

            const marketItem = eventMarkets[0];
            if (marketItem === undefined) {
                return true;
            }

            const market = this.common.models.getMarket(marketItem.id);

            if (market === null) {
                return true;
            }

            return false;
        }

        return true;
    }

    @computed public get eventsCollection(): EventsCollectionList {
        return getEventsForSportType(this.common, this.activeSportTab, this.configComponents.config.isBetGliderActive);
    }

    @computed public get eventsList(): Array<EventListGroupEventItemType> {
        const events = this.eventsCollection.eventItems ?? [];
        const eventsSorted = sortSmallEventsByTime(events);
        return eventsSorted;
    }

    @computed public get eventIds(): Array<EventId> {
        return this.eventsList.slice(0, this.page * this.pageSize).map((event) => event.id2);
    }

    @computed public get eventIdsUkIreland(): Array<EventId> {
        return this.eventsList
            .filter((event) => event.tags['country'] === 'GB' || event.tags['country'] === 'IE')
            .slice(0, this.page * this.pageSize)
            .map((event) => event.id2);
    }

    @computed public get eventModels(): Array<EventModel> {
        return this.eventIds.map((eventId) => eventId.getEventModel()).filter((eventModel) => eventModel !== null);
    }
    @computed public get eventModelsUkIreland(): Array<EventModel> {
        return this.eventIdsUkIreland
            .map((eventId) => eventId.getEventModel())
            .filter((eventModel) => eventModel !== null);
    }

    @computed public get racingEventsForViewModels(): Array<EventModel> {
        const shouldDisplayStartedRaces =
            this.configComponents.config.isBetGliderActive && this.activeSportTab === 'horseracing';

        return this.eventModels.filter((event) =>
            isRacingEventVisibleForNextRacesCarousel(event, shouldDisplayStartedRaces)
        );
    }

    @computed public get racingEventsForViewModelsUkIreland(): Array<EventModel> {
        const shouldDisplayStartedRaces =
            this.configComponents.config.isBetGliderActive && this.activeSportTab === 'horseracing';

        return this.eventModelsUkIreland.filter((event) =>
            isRacingEventVisibleForNextRacesCarousel(event, shouldDisplayStartedRaces)
        );
    }

    @action public loadMore = async (): Promise<void> => {
        this.page++;

        for (const eventId of this.eventIds) {
            await eventId.getEventModelAsync();
        }
    };

    @action public resetPagination = (): void => {
        this.page = 1;
    };
}

/* ----------> NEXT RACES STATE <---------- */
export class NextRacesState {
    @observable public isNextRacesOpen: boolean = true;
    @observable public isRacesRegionSelected: boolean;
    @observable private selectedSport: RaceSportType = 'horseracing';

    public readonly sliderState: SliderState;

    public constructor(
        private readonly common: Common,
        private readonly sportPage?: RaceSportType
    ) {
        makeObservable(this);
        this.sliderState = new SliderState(
            (): number => this.eventsCount,
            (): void => this.loadMore()
        );
        const config = ConfigComponents.get(this.common).config;
        this.isRacesRegionSelected = config.isNextRacesToggleActive;
    }

    @computed public get activeSportTab(): RaceSportType {
        return this.sportPage === undefined ? this.selectedSport : this.sportPage;
    }

    @computed public get activeRegion(): RacesRegionType {
        if (this.isRacesRegionSelected && this.selectedSport === 'horseracing') {
            return 'uk&ireland';
        } else {
            return 'all';
        }
    }

    @computed public get racingEventsListToDisplayReady(): Array<EventModel> {
        if (this.activeRegion === 'uk&ireland') {
            return this.racingEventsForViewModelsUkIreland;
        }
        return this.racingEventsForViewModelsAll;
    }

    public get eventsCount(): number {
        return this.racingEventsListToDisplayReady.length;
    }

    @computed public get racingEventsForViewModelsUkIreland(): Array<EventModel> {
        return SliderDataState.get(this.common, this.activeSportTab).racingEventsForViewModelsUkIreland;
    }

    @computed public get racingEventsForViewModelsAll(): Array<EventModel> {
        return SliderDataState.get(this.common, this.activeSportTab).racingEventsForViewModels;
    }

    @computed public get hideToggle(): boolean {
        if (this.isLoadingEventsCollectionEarly || this.racingEventsForViewModelsUkIreland.length === 0) {
            return true;
        }

        return false;
    }

    @computed public get racesTypeFilterButtons(): Array<FiltersType> {
        const languagesState = LanguagesState.get(this.common);
        return [
            {
                key: 'horseracing',
                label: languagesState.getTranslation('next-off.switch-button.horseracing', 'Horse Racing'),
                onClick: (): void => {
                    this.onSportTabChange('horseracing');
                },
            },
            {
                key: 'greyhoundracing',
                label: languagesState.getTranslation('next-off.switch-button.greyhoundracing', 'Greyhound'),
                onClick: (): void => {
                    this.onSportTabChange('greyhoundracing');
                },
            },
        ];
    }

    @action public onSportTabChange = (sport: RaceSportType): void => {
        this.selectedSport = sport;
        this.sliderState.resetSlider();
    };

    @action public toggleNextRacesView = (): void => {
        this.isNextRacesOpen = !this.isNextRacesOpen;
    };

    @action public toggleRacesRegion = (): void => {
        this.isRacesRegionSelected = !this.isRacesRegionSelected;
        this.sliderState.resetSlider();

        const sliderDataState = SliderDataState.get(this.common, this.activeSportTab);
        sliderDataState.resetPagination();
    };

    @action public loadMore = (): void => {
        const sliderDataState = SliderDataState.get(this.common, this.activeSportTab);
        sliderDataState.loadMore();
    };

    @computed public get eventIds(): Array<EventId> {
        return SliderDataState.get(this.common, this.activeSportTab).eventIds;
    }

    @computed public get eventModels(): Array<EventModel> {
        return SliderDataState.get(this.common, this.activeSportTab).eventModels;
    }

    @computed public get isLoadingEventsCollectionEarly(): boolean {
        return SliderDataState.get(this.common, this.activeSportTab).isLoadingEventsCollectionEarly;
    }
}
